/**
 * KAN-243: Survey question definitions.
 * Pure structural data — no display strings. All labels come from translations.
 */

export type QuestionType = 'radio' | 'checkbox' | 'slider' | 'textarea' | 'email';

export interface QuestionOption {
  value: string;
  /** When true, selecting this option reveals a text input for detail */
  triggersInput?: boolean;
}

export interface SurveyQuestion {
  id: string;
  /** Short numeric label for progress (e.g. "1") */
  number: number;
  type: QuestionType;
  options?: QuestionOption[];
  /** For checkbox: max selections allowed */
  maxSelections?: number;
  /** For slider: min/max range */
  sliderMin?: number;
  sliderMax?: number;
  /** Whether the question must be answered to submit */
  required: boolean;
  /** Whether this question has a hint text in translations */
  hasHint?: boolean;
}

export const SURVEY_QUESTIONS: SurveyQuestion[] = [
  {
    id: 'q1_dog_count',
    number: 1,
    type: 'radio',
    required: true,
    options: [
      { value: '1' },
      { value: '2' },
      { value: '3+' },
      { value: 'planning' },
    ],
  },
  {
    id: 'q2_biggest_worry',
    number: 2,
    type: 'radio',
    required: true,
    options: [
      { value: 'severity' },
      { value: 'cost' },
      { value: 'wait' },
      { value: 'communication' },
      { value: 'other_q2', triggersInput: true },
    ],
  },
  {
    id: 'q3_first_action',
    number: 3,
    type: 'radio',
    required: true,
    options: [
      { value: 'google' },
      { value: 'vet_call' },
      { value: 'forum' },
      { value: 'friends' },
      { value: 'wait_see' },
    ],
  },
  {
    id: 'q4_valuable_features',
    number: 4,
    type: 'checkbox',
    required: true,
    maxSelections: 3,
    options: [
      { value: 'ai_chat' },
      { value: 'health_record' },
      { value: 'reminders' },
      { value: 'vet_summary' },
      { value: 'breed_info' },
      { value: 'multi_dog' },
      { value: 'weight_diet' },
    ],
  },
  {
    id: 'q5_current_tracking',
    number: 5,
    type: 'radio',
    required: true,
    options: [
      { value: 'no' },
      { value: 'mental' },
      { value: 'notes' },
      { value: 'app', triggersInput: true },
    ],
  },
  {
    id: 'q6_why_stopped',
    number: 6,
    type: 'radio',
    required: true,
    options: [
      { value: 'too_manual' },
      { value: 'forgot' },
      { value: 'no_value' },
      { value: 'never_tried' },
      { value: 'still_tracking' },
    ],
  },
  {
    id: 'q7_ai_comfort',
    number: 7,
    type: 'slider',
    required: true,
    hasHint: true,
    sliderMin: 1,
    sliderMax: 5,
  },
  {
    id: 'q8_trust_factors',
    number: 8,
    type: 'checkbox',
    required: true,
    maxSelections: 3,
    options: [
      { value: 'citations' },
      { value: 'vet_endorsed' },
      { value: 'transparency' },
      { value: 'no_diagnosis' },
      { value: 'data_privacy' },
      { value: 'track_record' },
    ],
  },
  {
    id: 'q9_willingness_to_pay',
    number: 9,
    type: 'radio',
    required: true,
    options: [
      { value: 'free_only' },
      { value: 'under_5' },
      { value: '5_10' },
      { value: 'over_10' },
      { value: 'depends' },
    ],
  },
  {
    id: 'q10_open_feedback',
    number: 10,
    type: 'textarea',
    required: false,
    hasHint: true,
  },
];

/** Total required questions (for progress bar) */
export const REQUIRED_QUESTION_COUNT = SURVEY_QUESTIONS.filter((q) => q.required).length;
